<template>
  <li
    class="list-item"
    :class="{
      disabled: option.disabled,
      selected: selected,
      'has-active-children': hasActiveChildren,
      'no-row-styles': noRowStyles,
      'font-bold': bold,
      [`variant-${variant}`]: true,
    }"
    role="button"
    :tabindex="option.disabled ? -1 : 0"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <div class="flex items-center gap-8 w-full">
      <BittsCheckbox
        v-if="showCheckbox"
        :checked="selected"
        :disabled="option.disabled"
        @input="emit('checkbox-change', $event)"
        @click.stop
      />

      <slot name="prefix">
        <FontAwesomeIcon
          v-if="faIcon"
          :icon="faIcon"
          class="text-neutral-text-weak"
          :style="{ width: '16px', height: '16px' }"
        />
      </slot>

      <span class="option-label truncate flex-1 text-left">
        <slot>{{ option.label }}</slot>
      </span>

      <FontAwesomeIcon
        v-if="!showCheckbox && selected"
        :icon="['fak', 'check']"
        :class="
          variant === 'upsell' ? 'text-secondary-accent' : 'text-neutral-accent'
        "
        :style="{ width: '16px', height: '16px' }"
      />

      <slot name="suffix" />
    </div>
  </li>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import BittsCheckbox from '../BittsCheckbox/BittsCheckbox.vue';
import type { BittsIcon } from '../types';

type OptionType = {
  label: string;
  value: unknown;
  disabled?: boolean;
};

const props = withDefaults(
  defineProps<{
    option: OptionType;
    selected?: boolean;
    hasActiveChildren?: boolean;
    showCheckbox?: boolean;
    noRowStyles?: boolean;
    bold?: boolean;
    variant?: 'default' | 'upsell';
    faIcon?: BittsIcon;
  }>(),
  {
    selected: false,
    hasActiveChildren: false,
    showCheckbox: false,
    noRowStyles: false,
    bold: false,
    variant: 'default',
    faIcon: null,
  },
);

const emit = defineEmits<{
  click: [event: MouseEvent | KeyboardEvent];
  'checkbox-change': [checked: boolean];
}>();

function handleClick(event: MouseEvent | KeyboardEvent) {
  if (props.option.disabled) return;
  emit('click', event);
}
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";

.list-item {
  @apply px-8 py-4 rounded-6 cursor-pointer text-neutral-text-strong transition-colors text-base;
}

.list-item.disabled {
  @apply cursor-not-allowed opacity-50;
}

.list-item.variant-default.selected:not(.no-row-styles) {
  @apply bg-neutral-background;
}

.list-item.variant-default:hover:not(.disabled),
.list-item.variant-default.has-active-children:not(.disabled) {
  @apply bg-neutral-background-weak;
}

.list-item.variant-upsell.selected:not(.no-row-styles) {
  @apply bg-secondary-background-weak;
}

.list-item.variant-upsell:hover:not(.disabled),
.list-item.variant-upsell.has-active-children:not(.disabled) {
  @apply bg-secondary-background-weak;
}

.option-label {
  @apply text-neutral-text-strong;
}
</style>
